import { useState } from "react";
import { MessageSquareText, Loader2, Send, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

interface ExplanationResult {
  query: string;
  breakdown: {
    operator: string;
    meaning: string;
  }[];
  overallPurpose: string;
  potentialResults: string;
  tips: string[];
}

export function DorkExplainer() {
  const { t } = useLocale();
  const [input, setInput] = useState("");
  const [isExplaining, setIsExplaining] = useState(false);
  const [result, setResult] = useState<ExplanationResult | null>(null);

  const handleExplain = async () => {
    if (!input.trim()) {
      toast({
        title: t("explainer.input_required"),
        description: t("explainer.input_required_desc"),
        variant: "destructive",
      });
      return;
    }

    setIsExplaining(true);
    setResult(null);

    try {
      const response = await supabase.functions.invoke("explain-dork", {
        body: { query: input.trim() },
      });

      if (response.error) throw new Error(response.error.message);
      setResult(response.data);
    } catch (error) {
      console.error("Explanation error:", error);
      toast({
        title: t("explainer.failed"),
        description: error instanceof Error ? error.message : t("explainer.failed"),
        variant: "destructive",
      });
    } finally {
      setIsExplaining(false);
    }
  };

  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
          <Brain className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{t("explainer.title")}</h3>
          <p className="text-xs text-muted-foreground">{t("explainer.subtitle")}</p>
        </div>
      </div>

      <div className="relative">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleExplain()}
          placeholder={t("explainer.placeholder")}
          className="w-full px-4 py-3 bg-cyber-darker border border-border/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 pr-24"
        />
        <button
          onClick={handleExplain}
          disabled={isExplaining}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-medium disabled:opacity-50 transition-opacity"
        >
          {isExplaining ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          {t("explainer.button")}
        </button>
      </div>

      {result && (
        <div className="space-y-4 animate-fade-in">
          <div className="p-3 rounded-lg bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
            <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">
              {t("explainer.purpose")}
            </span>
            <p className="text-sm text-foreground mt-1">{result.overallPurpose}</p>
          </div>

          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("explainer.breakdown")}
            </span>
            <div className="space-y-2">
              {result.breakdown.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-2 rounded-lg bg-cyber-darker border border-border/30"
                >
                  <code className="text-xs font-mono font-bold text-primary whitespace-nowrap">
                    {item.operator}
                  </code>
                  <span className="text-sm text-foreground/80">{item.meaning}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("explainer.expected")}
            </span>
            <p className="text-sm text-foreground/80 mt-1">{result.potentialResults}</p>
          </div>

          {result.tips.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("explainer.tips")}
              </span>
              <ul className="space-y-1">
                {result.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground/70">
                    <MessageSquareText className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
